import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Calendar, Clock, User, Edit, Trash2 } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import TutoringCalendar from "@/components/tutoring/TutoringCalendar";
import UpcomingSessionsGrid from "@/components/scheduling/UpcomingSessionsGrid";
import { ListToolbar } from "@/components/common/ListToolbar";
import { DataPagination } from "@/components/common/DataPagination";
import { LoadingState } from "@/components/common/LoadingState";
import { ErrorState } from "@/components/common/ErrorState";
import { EmptyState } from "@/components/common/EmptyState";
import { usePaginatedQuery } from "@/hooks/usePaginatedQuery";
import { queryKeys } from "@/lib/queryKeys";
import { useUrlState } from "@/lib/urlState";
import { exportRowsAsCsv } from "@/lib/csv";
import { toast } from "sonner";
import { format } from "date-fns";

interface TutoringSchedule {
  id: string;
  student_email: string;
  student_name: string | null;
  session_date: string;
  start_time: string;
  end_time: string;
  subject: string;
  topic: string | null;
  notes: string | null;
  status: string;
  created_at: string;
}

const formatTime = (time: string) => {
  const [hours, minutes] = time.split(":");
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? "PM" : "AM";
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minutes} ${ampm}`;
};

const getStatusColor = (
  status: string,
): "default" | "secondary" | "destructive" | "outline" => {
  switch (status) {
    case "scheduled":
      return "default";
    case "completed":
      return "secondary";
    case "cancelled":
      return "destructive";
    case "rescheduled":
      return "outline";
    default:
      return "default";
  }
};

export default function InstructorTutoringSchedules() {
  const { user } = useAuth();
  const url = useUrlState();
  const queryClient = useQueryClient();

  const tab = url.get("tab") ?? "grid";
  const search = url.get("q") ?? "";
  const statusFilter = url.get("status") ?? "upcoming"; // upcoming | past | all
  const todayIso = new Date().toISOString().slice(0, 10);

  const paginated = usePaginatedQuery<TutoringSchedule>({
    queryKey: queryKeys.tutoringSchedules.list({
      page: 0,
      pageSize: 25,
      filters: { role: "instructor", search, statusFilter, userId: user?.id },
    }),
    table: "tutoring_schedules",
    honorSoftDelete: true,
    orderBy: { column: "session_date", ascending: statusFilter !== "past" },
    syncToUrl: tab === "list",
    enabled: !!user && tab === "list",
    filters: (q) => {
      let next = q.eq("instructor_id", user!.id);
      if (statusFilter === "upcoming") {
        next = next.gte("session_date", todayIso).eq("status", "scheduled");
      } else if (statusFilter === "past") {
        next = next.lt("session_date", todayIso);
      }
      if (search) {
        next = next.or(
          `student_name.ilike.%${search}%,student_email.ilike.%${search}%,subject.ilike.%${search}%,topic.ilike.%${search}%`,
        );
      }
      return next;
    },
  });

  useEffect(() => {
    paginated.setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.tutoringSchedules.all });

  const deleteSchedule = async (scheduleId: string) => {
    try {
      const { error } = await supabase
        .from("tutoring_schedules")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", scheduleId);
      if (error) throw error;
      toast.success("Schedule deleted successfully");
      refresh();
    } catch (error) {
      console.error("Error deleting schedule:", error);
      toast.error("Failed to delete schedule");
    }
  };

  const handleExport = () => {
    if (paginated.rows.length === 0) {
      toast.error("Nothing to export on this page");
      return;
    }
    exportRowsAsCsv(
      `tutoring-schedules-${statusFilter}.csv`,
      paginated.rows as unknown as Record<string, unknown>[],
      [
        "session_date",
        "start_time",
        "end_time",
        "subject",
        "topic",
        "student_name",
        "student_email",
        "status",
      ],
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <Header />

      <main className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Tutoring Schedules
            </h1>
            <p className="text-muted-foreground">
              Create and manage your tutoring sessions with students.
            </p>
          </div>
          <Button asChild>
            <Link to="/instructor/tutoring/create">
              <Plus className="h-4 w-4 mr-2" />
              Schedule Session
            </Link>
          </Button>
        </div>

        <Tabs
          value={tab}
          onValueChange={(v) => url.set({ tab: v, page: null })}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="grid">Grid View</TabsTrigger>
            <TabsTrigger value="list">List View</TabsTrigger>
            <TabsTrigger value="calendar">Calendar View</TabsTrigger>
          </TabsList>

          <TabsContent value="grid" className="mt-6">
            <UpcomingSessionsGrid />
          </TabsContent>

          <TabsContent value="calendar" className="mt-6">
            <TutoringCalendar instructorId={user?.id || ""} isInstructor={true} />
          </TabsContent>

          <TabsContent value="list" className="mt-6 space-y-4">
            <ListToolbar
              search={search}
              onSearchChange={(v) => url.set({ q: v || null, page: null })}
              searchPlaceholder="Search student, subject, or topic…"
              onExportCsv={handleExport}
              filters={
                <Select
                  value={statusFilter}
                  onValueChange={(v) => url.set({ status: v, page: null })}
                >
                  <SelectTrigger className="w-40 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="upcoming">Upcoming</SelectItem>
                    <SelectItem value="past">Past</SelectItem>
                    <SelectItem value="all">All sessions</SelectItem>
                  </SelectContent>
                </Select>
              }
            />

            {paginated.isError ? (
              <ErrorState onRetry={() => paginated.refetch()} />
            ) : paginated.isLoading ? (
              <LoadingState />
            ) : paginated.rows.length === 0 ? (
              <EmptyState
                title="No sessions"
                description={
                  statusFilter === "upcoming"
                    ? "You have no upcoming sessions. Schedule one to get started."
                    : "No sessions match your filters."
                }
              />
            ) : (
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    {paginated.rows.map((schedule) => (
                      <div
                        key={schedule.id}
                        className="flex items-center justify-between p-4 border rounded-lg bg-card"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h3 className="font-medium">
                              {schedule.student_name || schedule.student_email}
                            </h3>
                            <Badge variant={getStatusColor(schedule.status)}>
                              {schedule.status.charAt(0).toUpperCase() +
                                schedule.status.slice(1)}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {format(new Date(schedule.session_date), "MMM dd, yyyy")}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatTime(schedule.start_time)} -{" "}
                              {formatTime(schedule.end_time)}
                            </span>
                            <Badge variant="outline">{schedule.subject}</Badge>
                            {schedule.topic && <span>Topic: {schedule.topic}</span>}
                          </div>
                          {schedule.notes && (
                            <p className="text-sm text-muted-foreground mt-2">
                              {schedule.notes}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" asChild>
                            <Link to={`/instructor/tutoring/edit/${schedule.id}`}>
                              <Edit className="h-4 w-4" />
                            </Link>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteSchedule(schedule.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <DataPagination
              page={paginated.page}
              pageSize={paginated.pageSize}
              total={paginated.total}
              pageCount={paginated.pageCount}
              onPageChange={paginated.setPage}
              onPageSizeChange={paginated.setPageSize}
              isFetching={paginated.isFetching}
            />
          </TabsContent>
        </Tabs>
      </main>

      <Footer />
    </div>
  );
}
